import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Table, Collapse, Button, Tooltip, Space, Modal } from "antd";
import moment from "moment";
import NumberFormat from "react-number-format";
import { toDisplayFormat } from "../../../lib/utils/formatDate";
import preconfirmIcon from "../../../img/preconfirm-icon.svg";
import resetfreshIcon from "../../../img/resetfresh-icon.svg";
import { inputClearIcon } from "../../../lib/icons";
import SpanNumberFormat from "../../common/SpanNumberFormat";
import { round } from '../../../lib/utils';

const AgreementHolderLine = ({
  agreementHolder,
  isEditable,
  handleUpdateAgreementHolder,
  agreementAmountLeft,
}) => {
  const [tableData, setTableData] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [collapseActiveKey, setCollapseActiveKey] = useState([]);
  const [totalAmountChecked, setTotalAmountChecked] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [inputValuesBeforeEdit, setInputValuesBeforeEdit] = useState({});
  const [isUpdatingAgreementHolder, setIsUpdatingAgreementHolder] = useState(false);
  const ref = useRef();

  // Раскрытие счетов по договорам, с разнесенными платежами
  useEffect(() => {
    let hasGoal = Boolean(agreementHolder.balanceAmount);
    agreementHolder.goalHolders.forEach((goalHolder) => {
      if (goalHolder.goal?.amount) {
        hasGoal = true;
      }
    });
    if (hasGoal) {
      setCollapseActiveKey([0]);
    }
  }, []);

  // console.log({ totalAmountChecked });
  // console.log(tableData);
  // console.log(agreementHolder);
  // if (isEditing) console.log({ inputValues });

  const sortGoalHolders = ({ bill: billA }, { bill: billB }) => {
    if (moment(billA?.periodFrom).isSame(billB?.periodFrom, "day")) {
      return billA?.billNumber > billB?.billNumber ? 1 : -1;
    }
    if (moment(billA?.periodFrom).isAfter(billB?.periodFrom)) {
      return 1;
    }
    return -1;
  };

  const getTotalConfirmedSum = () => {
    let sum = 0;
    Object.keys(inputValues).forEach((key) => {
      if (inputValues[key].isConfirmed) {
        sum += Number(inputValues[key].amount) * 100;
      }
    });
    return sum / 100;
  };

  const getTotalSum = () => {
    let sum = 0;
    Object.keys(inputValues).forEach((key) => {
      const value = Number(inputValues[key].amount) * 100;
      if (value) sum += value;
    });
    return sum / 100;
  };

  const getSubmitDisable = () => {
    if (!isEditing) return true;
    const totalSum = getTotalSum();
    const totalConfirmedSum = getTotalConfirmedSum();
    return totalSum > 0 && totalSum !== totalConfirmedSum;
  };

  const getCurrentAgreementAmountLeft = () => {
    const curentAmount = Number(agreementAmountLeft) * 100 - getTotalConfirmedSum() * 100;
    return round(curentAmount) / 100;
  };

  // Заполнение tableData и исходных оплат в inputValues
  useEffect(() => {
    const values = {};
    agreementHolder.goalHolders
      .filter(({ bill }) => Boolean(bill?.id))
      .forEach(({ bill, goal }) => {
        if (goal?.amount) {
          values[bill.id] = { amount: Number(goal.amount), isConfirmed: true };
        }
      });

    if (agreementHolder.balanceAmount) {
      values.balanceAmount = { amount: Number(agreementHolder.balanceAmount), isConfirmed: true };
    }

    setInputValues(values);

    const sortedGoalHolders = agreementHolder.goalHolders
      .filter(({ bill }) => Boolean(bill?.id))
      .sort(sortGoalHolders);
    setTableData(
      sortedGoalHolders.map((goalHolder, i) => ({
        ...goalHolder,
        key: i,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeInput = (billId, inputValue) => {
    setInputValues((prev) => ({
      ...prev,
      [billId]: { amount: inputValue, isConfirmed: false },
    }));
  };

  const inputRestAmount = (billId, restAmount) => {
    setInputValues((prev) => ({
      ...prev,
      [billId]: {
        isConfirmed: false,
        amount: restAmount,
      },
    }));
  };

  const inputRestBalanceAmount = () => {
    const amount = getCurrentAgreementAmountLeft() + (inputValues.balanceAmount?.amount || 0);
    setInputValues((prev) => ({
      ...prev,
      balanceAmount: {
        isConfirmed: false,
        amount,
      },
    }));
  };

  const checkAmount = ({ currentAmount, restAmount, billId }) => {
    if (currentAmount > restAmount) {
      Modal.warning({
        content: "Распределенная сумма не может быть больше остатка платежа",
        onOk: () => inputRestAmount(billId, restAmount),
        onCancel: () => inputRestAmount(billId, restAmount),
      });
      return false;
    }
    if (restAmount === 0) {
      Modal.warning({
        content: "Остаток платежа равен 0",
      });
      return false;
    }
    return true;
  };

  const confirmValue = (billId, amountLeft) => {
    if (inputValues[billId]?.isConfirmed) return;
    const currentAmount = Number(inputValues[billId]?.amount) || 0;
    const restAmount = Math.min(getCurrentAgreementAmountLeft(), amountLeft);
    if (checkAmount({ currentAmount, restAmount, billId }) === false) return;
    setInputValues((prev) => ({
      ...prev,
      [billId]: {
        amount: currentAmount > 0 ? currentAmount : restAmount,
        isConfirmed: true,
      },
    }));
  };

  const confirmBalanceValue = () => {
    if (inputValues.balanceAmount?.isConfirmed) return;
    setTotalAmountChecked(true);
    const currentAmount = Number(inputValues.balanceAmount?.amount) || 0;
    const restAmount = getCurrentAgreementAmountLeft();
    if (checkAmount({ currentAmount, restAmount, billId: "balanceAmount" }) === false) return;
    setInputValues((prev) => ({
      ...prev,
      balanceAmount: {
        amount: currentAmount > 0 ? currentAmount : restAmount,
        isConfirmed: true,
      },
    }));
  };

  const cancelConfirmedValue = (billId) => {
    if (!inputValues[billId]?.isConfirmed) return;
    if (billId === "balanceAmount") {
      setTotalAmountChecked(false);
    }
    setInputValues((prev) => {
      const { amount } = prev[billId];
      return {
        ...prev,
        [billId]: {
          amount,
          isConfirmed: false,
        },
      };
    });
  };

  const onSubmit = () => {
    setIsUpdatingAgreementHolder(true);
    setModalVisible(false);
    const goalHolders = agreementHolder.goalHolders
      .filter(({ bill }) => Boolean(bill?.id))
      .filter((goalHolder) => inputValues[goalHolder.bill.id]?.isConfirmed)
      .map((goalHolder) => ({
        ...goalHolder,
        goal: {
          ...goalHolder.goal,
          amount: inputValues[goalHolder.bill.id].amount,
        },
      }));
    const balanceAmount = inputValues.balanceAmount?.isConfirmed
      ? inputValues.balanceAmount.amount
      : "";
    handleUpdateAgreementHolder({
      ...agreementHolder,
      goalHolders,
      balanceAmount,
    }).catch(() => {
      setIsUpdatingAgreementHolder(false);
    });
  };

  const handleSubmit = () => {
    if (!totalAmountChecked && getTotalConfirmedSum() < agreementAmountLeft) {
      setModalVisible(true);
      return;
    }
    onSubmit();
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const confirmModal = async () => {
    await closeModal();
    await inputRestBalanceAmount();
    ref.current.focus();
  };

  const columns = [
    {
      title: "Номер счета",
      dataIndex: "billNumber",
      key: "1",
      render: (_, { bill }) => (bill.billNumber ? bill.billNumber : ""),
    },
    {
      title: "Период",
      dataIndex: "period",
      key: "2",
      render: (_, { bill }) => {
        if (bill.periodFrom && bill.periodTo) {
          return `${toDisplayFormat(moment(bill.periodFrom))} - ${toDisplayFormat(
            moment(bill.periodTo)
          )}`;
        }
        return null;
      },
    },
    {
      title: "Сумма счета",
      dataIndex: "amount",
      key: "3",
      render: (_, { bill }) => (bill.amount ? SpanNumberFormat({ amount: bill.amount }) : ""),
    },
    {
      title: "Неоплаченная сумма",
      dataIndex: "amountLeft",
      key: "4",
      render: (_, { bill }) =>
        bill.amountLeft ? SpanNumberFormat({ amount: bill.amountLeft }) : "",
    },
    {
      title: "Сумма к оплате",
      dataIndex: "goalAmount",
      key: "5",
      render: (_, { bill }) => {
        const billId = bill.id;
        const amount = inputValues[billId]?.amount || "";
        const handleConfirm = () => confirmValue(billId, Number(bill.amountLeft));
        const isDisabled = inputValues[billId]?.isConfirmed;
        if (isEditing) {
          return (
            <Row>
              <NumberFormat
                value={amount}
                thousandSeparator={" "}
                decimalScale={2}
                allowNegative={false}
                fixedDecimalScale={2}
                onValueChange={({ value }, { source }) => {
                  if (source === "prop") return;
                  onChangeInput(billId, value);
                }}
                disabled={isDisabled}
                className="ant-input-affix-wrapper inputNumber__hideArrows"
                style={{ width: "150px", marginRight: "15px" }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleConfirm();
                  }
                  return null;
                }}
              />
              <Button
                className="button-icon"
                icon={inputClearIcon()}
                onClick={() => onChangeInput(billId, 0)}
                htmlType="button"
                style={{
                  marginRight: "8px",
                  position: "relative",
                  zIndex: "10",
                  left: "-11px",
                  marginLeft: "-43px",
                  visibility: !amount || isDisabled ? "hidden" : "",
                }}
              />
              <Tooltip title="Подтвердить">
                <Button
                  className="button-icon"
                  icon={<img src={preconfirmIcon} alt="preconfirmIcon" />}
                  onClick={handleConfirm}
                  htmlType="button"
                  style={{ marginRight: "8px" }}
                />
              </Tooltip>
              <Tooltip title="Изменить">
                <Button
                  className="button-icon"
                  icon={<img src={resetfreshIcon} alt="resetfreshIcon" />}
                  htmlType="button"
                  onClick={() => cancelConfirmedValue(billId)}
                />
              </Tooltip>
            </Row>
          );
        }
        return amount ? SpanNumberFormat({ amount }) : "";
      },
      width: "270px",
    },
  ];

  const getPanelTitle = (agreementNumber) => (
    <>
      <Row>
        <Col className="label" span={4}>
          Договор
        </Col>
        <Col span={8} style={{ whiteSpace: "wrap" }}>
          {agreementNumber}
        </Col>
      </Row>
    </>
  );

  const getBtnTooltipTitle = () => {
    if (!isEditing) return "";
    if (getSubmitDisable()) return "Подтвердите или удалите введенные значения";
    return "";
  };

  const handleEditing = () => {
    if (isEditing) {
      setIsEditing(false);
      setInputValues(inputValuesBeforeEdit);
    } else {
      setIsEditing(true);
      setInputValuesBeforeEdit(inputValues);
    }
  };

  const getFooter = () => {
    const amount = inputValues.balanceAmount?.amount || "";
    const isDisabled = inputValues.balanceAmount?.isConfirmed;
    return (
      <Row>
        <Col span={14}>
          <span style={{ color: "#8A99A8", marginRight: "20px", lineHeight: "32px" }}>
            Сумма на баланс
          </span>
          {!isEditing && (
            <span style={{ display: "inline-block", width: "150px", marginRight: "15px" }}>
              {amount ? SpanNumberFormat({ amount }) : ""}
            </span>
          )}
          {isEditing && (
            <>
              <NumberFormat
                thousandSeparator={" "}
                decimalScale={2}
                allowNegative={false}
                fixedDecimalScale={2}
                getInputRef={ref}
                disabled={isDisabled}
                value={amount}
                onValueChange={({ value }, { source }) => {
                  if (source === "prop") return;
                  onChangeInput("balanceAmount", value);
                }}
                className="ant-input-affix-wrapper inputNumber__hideArrows"
                style={{ width: "150px", marginRight: "15px" }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    confirmBalanceValue();
                  }
                  return null;
                }}
              />
              <Button
                className="button-icon"
                icon={inputClearIcon()}
                onClick={() => onChangeInput("balanceAmount", 0)}
                htmlType="button"
                style={{
                  marginRight: "8px",
                  position: "relative",
                  zIndex: "10",
                  left: "-11px",
                  marginLeft: "-43px",
                  visibility: !amount || isDisabled ? "hidden" : "",
                }}
              />
              <Tooltip title="Подтвердить">
                <Button
                  className="button-icon"
                  icon={<img src={preconfirmIcon} alt="preconfirmIcon" />}
                  onClick={confirmBalanceValue}
                  htmlType="button"
                  style={{ marginRight: "8px" }}
                />
              </Tooltip>
              <Tooltip title="Изменить">
                <Button
                  className="button-icon"
                  icon={<img src={resetfreshIcon} alt="resetfreshIcon" />}
                  htmlType="button"
                  onClick={() => cancelConfirmedValue("balanceAmount")}
                />
              </Tooltip>
            </>
          )}
        </Col>
        <Col span={10}>
          <Space>
            <Button onClick={handleEditing}>
              {isEditing ? "Отменить редактирование" : "Редактировать данные"}
            </Button>
            <Tooltip title={getBtnTooltipTitle()}>
              <Button
                type="primary"
                onClick={handleSubmit}
                disabled={getSubmitDisable()}
                loading={isUpdatingAgreementHolder}
              >
                Подтвердить
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>
    );
  };

  const getRestAmountModal = () => {
    const buttonStyle = {
      width: "108px",
    };
    return (
      <Modal
        visible={modalVisible}
        bodyStyle={{ height: "153px" }}
        width="500px"
        footer={[
          <Button key="1" style={buttonStyle} onClick={onSubmit}>
            Нет
          </Button>,
          <Button key="2" style={buttonStyle} onClick={confirmModal} type="primary">
            Ок
          </Button>,
        ]}
        onCancel={closeModal}
      >
        <div style={{ textAlign: "center", marginTop: "50px", fontSize: "16px" }}>
          Вы хотите распределить остаток суммы на баланс?
        </div>
      </Modal>
    );
  };

  return (
    <>
      <Row>
        <Col span={24}>
          <Collapse
            expandIconPosition="right"
            activeKey={collapseActiveKey}
            onChange={setCollapseActiveKey}
            collapsible={isEditing ? "disabled" : ""}
            className="collapse__hideBottomBorder"
          >
            <Collapse.Panel
              header={getPanelTitle(agreementHolder.agreement?.agreementNumber)}
              key={0}
            >
              <Table
                columns={columns}
                dataSource={tableData}
                pagination={false}
                className="table__noHover tableEmpty__noMargin"
                footer={isEditable ? getFooter : null}
              />
            </Collapse.Panel>
          </Collapse>
        </Col>
      </Row>
      {getRestAmountModal()}
    </>
  );
};

export default AgreementHolderLine;
